import React, { useEffect, useState } from "react";
import { Alert, StyleSheet, View, ScrollView } from "react-native";
import ProjectRequirementsDashboard from "../../../components/project/ProjectRequirementsDashboard/ProjectRequirementsDashboard";
import AddTaskModal from "../../../components/project/AddTaskModal/AddTaskModal";
import EditTaskModal from "../../../components/project/EditTaskModal/EditTaskModal";
import DeleteTaskConfirmation from "../../../components/project/DeleteTaskConfirmation/DeleteTaskConfirmation";
import { useAppState, actions } from "../../../contexts/AppStateContext";
import {
  useMarkProjectRequirementCompleteMutation,
  useCreateProjectRequirementMutation,
  useUpdateProjectRequirementMutation,
  useDeleteProjectRequirementMutation,
  ProjectSubmission,
  ProjectRequirement,
  useSubmitProjectSubmissionFilesMutation,
  File,
  useDeleteProjectSubmissionMutation,
} from "../../../graphql/generated/graphql";
import AddProjectFile from "../../../components/project/AddProjectFileModal/AddProjectFileModal";
import { ResourceType } from "../../../contexts/ActiveCourseContext";

enum ModalState {
  ADD_PROJECT_FILE = "ADD_PROJECT_FILE",
  ADD_TASK = "ADD_TASK",
  EDIT_TASK = "EDIT_TASK",
  DELETE_TASK = "DELETE_TASK",
  CLOSED = "CLOSED",
}

interface ProjectRequirementsDashboardContainerProps {
  submission?: ProjectSubmission;
  refetchSubmission?: () => void;
}

const ProjectRequirementsDashboardContainer: React.FC<ProjectRequirementsDashboardContainerProps> = ({
  submission,
  refetchSubmission,
}) => {
  const { dispatch } = useAppState();

  const [modalState, setModalState] = useState<ModalState>(ModalState.CLOSED);

  const [selectedTask, setSelectedTask] = useState(null);

  const [files, setFiles] = useState<Partial<File>[]>([]);

  useEffect(() => {
    if (submission?.files) {
      setFiles(submission.files);
    }
  }, [submission]);

  // Mark requirement complete mutation
  const [markRequirementComplete, { loading: markingComplete }] = useMarkProjectRequirementCompleteMutation({
    onCompleted: (data) => {
      if (data.markProjectRequirementComplete.success) {
        // Refetch requirements to get updated data
        refetchSubmission();
      } else {
        Alert.alert("Error", data.markProjectRequirementComplete.message || "Failed to update requirement");
      }
    },
    onError: (error) => {
      console.error("Error marking requirement complete:", error);
      Alert.alert("Error", "Failed to update requirement. Please try again.");
    },
  });

  // Create requirement mutation
  const [createRequirement, { loading: creatingRequirement }] = useCreateProjectRequirementMutation({
    onCompleted: (data) => {
      if (data.createProjectRequirement.success) {
        dispatch({
          type: actions.SET_META,
          payload: { keypressIsListening: true },
        });
        setModalState(ModalState.CLOSED);
        refetchSubmission();
        Alert.alert("Success", "Task created successfully!");
      } else {
        Alert.alert("Error", data.createProjectRequirement.message || "Failed to create task");
      }
    },
    onError: (error) => {
      console.error("Error creating requirement:", error);
      Alert.alert("Error", "Failed to create task. Please try again.");
    },
  });

  // Update requirement mutation
  const [updateRequirement, { loading: updatingRequirement }] = useUpdateProjectRequirementMutation({
    onCompleted: (data) => {
      if (data.updateProjectRequirement.success) {
        dispatch({
          type: actions.SET_META,
          payload: { keypressIsListening: true },
        });
        setModalState(ModalState.CLOSED);
        setSelectedTask(null);
        refetchSubmission();
        Alert.alert("Success", "Task updated successfully!");
      } else {
        Alert.alert("Error", data.updateProjectRequirement.message || "Failed to update task");
      }
    },
    onError: (error) => {
      console.error("Error updating requirement:", error);
      Alert.alert("Error", "Failed to update task. Please try again.");
    },
  });

  // Delete requirement mutation
  const [deleteRequirement, { loading: deletingRequirement }] = useDeleteProjectRequirementMutation({
    onCompleted: (data) => {
      if (data.deleteProjectRequirement.success) {
        dispatch({
          type: actions.SET_META,
          payload: { keypressIsListening: true },
        });
        setModalState(ModalState.CLOSED);
        setSelectedTask(null);
        refetchSubmission();
        Alert.alert("Success", "Task deleted successfully!");
      } else {
        Alert.alert("Error", data.deleteProjectRequirement.message || "Failed to delete task");
      }
    },
    onError: (error) => {
      console.error("Error deleting requirement:", error);
      Alert.alert("Error", "Failed to delete task. Please try again.");
    },
  });

  const [submitProjectSubmissionFiles, { loading: submittingFiles }] = useSubmitProjectSubmissionFilesMutation({
    refetchQueries: ["GetProjectSubmission", "GetProjectSubmissionsByProject", "GetProjectSubmissionsByStudent"],
  });

  // Handle requirement completion toggle
  const handleMarkCompleted = async (requirementId: string, completed: boolean) => {
    try {
      if (completed) {
        // Mark as complete
        await markRequirementComplete({
          variables: { id: requirementId },
        });
      } else {
        // For unchecking, we need to use the update mutation
        // This would require implementing an updateProjectRequirement mutation
        Alert.alert("Info", "Unchecking completed requirements is not yet supported");
      }
    } catch (error) {
      console.error("Error updating requirement:", error);
      Alert.alert("Error", "Failed to update requirement. Please try again.");
    }
  };

  // Handle adding new task
  const handleAddTask = () => {
    setModalState(ModalState.ADD_TASK);
  };

  const handleAddProjectFile = () => {
    setModalState(ModalState.ADD_PROJECT_FILE);
  };

  // Handle editing task
  const handleEditTask = (requirement: ProjectRequirement) => {
    setSelectedTask(requirement);
    setModalState(ModalState.EDIT_TASK);
  };

  // Handle deleting task
  const handleDeleteTask = (requirementId: string) => {
    const task = submission?.requirements.find((req: ProjectRequirement) => req.id === requirementId);
    setSelectedTask(task);
    setModalState(ModalState.DELETE_TASK);
  };

  // Handle saving new task
  const handleSaveNewTask = async (taskData: ProjectRequirement) => {
    try {
      await createRequirement({
        variables: {
          requirement: {
            submissionId: submission?.id,
            title: taskData.title,
            description: taskData.description,
            type: taskData.type,
            priority: taskData.priority,
            estimatedHours: taskData.estimatedHours,
            dueDate: taskData.dueDate,
            dependencies: taskData.dependencies,
          },
        },
      });
    } catch (error) {
      console.error("Error creating task:", error);
      Alert.alert("Error", "Failed to create task. Please try again.");
    }
    setModalState(ModalState.CLOSED);
  };

  // Handle saving edited task
  const handleSaveEditedTask = async (taskData: ProjectRequirement) => {
    if (!selectedTask) return;

    try {
      await updateRequirement({
        variables: {
          id: selectedTask.id,
          requirement: {
            title: taskData.title,
            description: taskData.description,
            type: taskData.type,
            priority: taskData.priority,
            estimatedHours: taskData.estimatedHours,
            dueDate: taskData.dueDate,
            dependencies: taskData.dependencies,
            completed: taskData.completed,
          },
        },
      });
    } catch (error) {
      console.error("Error updating task:", error);
      Alert.alert("Error", "Failed to update task. Please try again.");
    }
    setModalState(ModalState.CLOSED);
  };

  // Handle confirming task deletion
  const handleConfirmDeleteTask = async () => {
    if (!selectedTask) return;

    try {
      await deleteRequirement({
        variables: { id: selectedTask.id },
      });
    } catch (error) {
      console.error("Error deleting task:", error);
      Alert.alert("Error", "Failed to delete task. Please try again.");
    }
    setModalState(ModalState.CLOSED);
  };

  const handleSubmitFiles = async () => {
    try {
      await submitProjectSubmissionFiles({
        variables: {
          submissionId: submission?.id,
          files: files.map((file) => ({ fileId: file.id, fileName: file.fileName || "" })),
        },
      });
    } catch (error) {
      console.error("Error submitting files:", error);
      Alert.alert("Error", "Failed to submit files. Please try again.");
    }
    setModalState(ModalState.CLOSED);
  };

  const handleCloseModal = () => {
    setModalState(ModalState.CLOSED);
    setSelectedTask(null);
  };

  return (
    <ScrollView style={css.container} showsVerticalScrollIndicator={true} nestedScrollEnabled={true}>
      <ProjectRequirementsDashboard
        submission={submission}
        onMarkCompleted={handleMarkCompleted}
        onAddTask={handleAddTask}
        onEditTask={handleEditTask}
        onDeleteTask={handleDeleteTask}
        onAddProjectFile={handleAddProjectFile}
        loading={markingComplete || creatingRequirement || updatingRequirement}
      />
      {/* Add Project File Modal */}
      <AddProjectFile
        visible={modalState === ModalState.ADD_PROJECT_FILE}
        onClose={handleCloseModal}
        fileState={[files, setFiles]}
        resource={{ resourceType: ResourceType.ACTIVITY_PROJECT, resourceId: submission?.id || "" }}
        onSubmitFiles={handleSubmitFiles}
      />

      {/* Add Task Modal */}
      <AddTaskModal
        visible={modalState === ModalState.ADD_TASK}
        onClose={handleCloseModal}
        onSave={handleSaveNewTask}
        loading={creatingRequirement}
      />
      {/* Edit Task Modal */}
      <EditTaskModal
        visible={modalState === ModalState.EDIT_TASK}
        onClose={handleCloseModal}
        onSave={handleSaveEditedTask}
        task={selectedTask}
        loading={updatingRequirement}
      />
      {/* Delete Task Confirmation */}
      <DeleteTaskConfirmation
        visible={modalState === ModalState.DELETE_TASK}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDeleteTask}
        task={selectedTask}
        loading={deletingRequirement}
      />
    </ScrollView>
  );
};

const css = StyleSheet.create({
  container: {
    height: "calc(100vh - 54px)",
    overflow: "hidden",
    paddingHorizontal: 80,
  },
});

export default ProjectRequirementsDashboardContainer;
